import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { apiFetch } from '../utils/api';
import QRCode from 'qrcode';
import AuthShell from './auth/AuthShell';
import AccessPending from './auth/AccessPending';
import ModalPortal from './ModalPortal';
import { toolAccessMap, isToolEntitled, canEditTool, matchToolKey, normalizeToolAccessList, productNavSections, SYSTEM_NAV_SECTIONS, sectionMatchesPath, firstAccessiblePath } from '../tools/catalog';
import SidebarNav from './SidebarNav';
import { ToolAccessContext } from '../tools/ToolAccessContext';
import ProjectHeaderControl from './ProjectHeaderControl';
import ruleStoreLogo from '../assets/rulestore_logo_large.png';
import { ChangelogPage, VersionButton } from '@product-changelog';
import { MaintenanceBanner } from '@maintenance-notice';
import PhoneNumberInput from './inputs/PhoneNumberInput';
import { PHONE_NUMBER_ERROR, isPhoneNumberValid } from './inputs/phoneNumber';

function parseJwt(token) {
    try {
        return JSON.parse(atob(token.split('.')[1]));
    } catch (e) {
        return null;
    }
}

const getInitials = (name) => {
    if (!name) return '';
    const parts = name.trim().split(/\s+/);
    if (parts.length === 0) return '';
    if (parts.length === 1) return parts[0].substring(0, 2).toUpperCase();
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

const EXPANDED_SECTIONS_KEY = 'sidebar-expanded-sections';
const COLLAPSE_ON_RECLICK_KEY = 'sidebar-collapse-on-reclick';
const CLOSE_OTHER_MENUS_KEY = 'sidebar-close-other-menus';

function readExpandedSections() {
    try {
        const raw = JSON.parse(localStorage.getItem(EXPANDED_SECTIONS_KEY) || '[]');
        return new Set(Array.isArray(raw) ? raw : []);
    } catch {
        return new Set();
    }
}

function writeExpandedSections(ids) {
    localStorage.setItem(EXPANDED_SECTIONS_KEY, JSON.stringify([...ids]));
}

export default function Layout() {
    const location = useLocation();
    const navigate = useNavigate();
    const [isCollapsed, setIsCollapsed] = useState(() => {
        return localStorage.getItem('sidebar-collapsed') === 'true';
    });
    const [expandedSectionIds, setExpandedSectionIds] = useState(readExpandedSections);

    const [isPopoverOpen, setIsPopoverOpen] = useState(false);
    const [isNotificationsOpen, setIsNotificationsOpen] = useState(false);
    const [isMessagesOpen, setIsMessagesOpen] = useState(false);
    const [isProfileModalOpen, setIsProfileModalOpen] = useState(false);
    const [profileActiveTab, setProfileActiveTab] = useState('general');
    const [isAdminModalOpen, setIsAdminModalOpen] = useState(false);
    const [adminActiveTab, setAdminActiveTab] = useState('org');

    // Mock notifications list
    const [notifications, setNotifications] = useState([
        {
            id: 1,
            title: "Rule Engine Updated",
            description: "Mathematical constraint resolution speed optimized by 15%.",
            time: "2 hours ago",
            unread: true,
            icon: "fa-solid fa-server",
            color: "var(--accent)"
        },
        {
            id: 2,
            title: "New Project Published",
            description: "Project 'Variant Manager V2' draft has been pushed to production.",
            time: "1 day ago",
            unread: true,
            icon: "fa-solid fa-cloud-arrow-up",
            color: "var(--success)"
        },
        {
            id: 3,
            title: "System Warning",
            description: "Variant verification found 1 conflict in 'Price Matrices'.",
            time: "3 days ago",
            unread: false,
            icon: "fa-solid fa-triangle-exclamation",
            color: "var(--error)"
        }
    ]);

    // Mock messages list
    const [messages, setMessages] = useState([
        {
            id: 1,
            sender: "Alexander Wright",
            avatarInitials: "AW",
            text: "Could you review the math constraints for the new variant configurations?",
            time: "10 mins ago",
            unread: true
        },
        {
            id: 2,
            sender: "Support Team",
            avatarInitials: "ST",
            text: "Your ticket #2890 (API performance latency) has been closed.",
            time: "4 hours ago",
            unread: false
        }
    ]);

    // Profile settings and security states
    const [profileData, setProfileData] = useState(null);
    const [profileLoading, setProfileLoading] = useState(false);
    const [profileSaving, setProfileSaving] = useState(false);
    const [profileSuccess, setProfileSuccess] = useState('');
    const [profileError, setProfileError] = useState('');

    // Profile inputs
    const [profileName, setProfileName] = useState('');
    const [profileTitle, setProfileTitle] = useState('');
    const [profileCompany, setProfileCompany] = useState('');
    const [profilePhone, setProfilePhone] = useState('');
    const [profileTheme, setProfileTheme] = useState(() => {
        try {
            return localStorage.getItem('theme-preference') || 'system';
        } catch {
            return 'system';
        }
    });
    const [collapseMenuOnReclick, setCollapseMenuOnReclick] = useState(() => {
        try {
            return localStorage.getItem(COLLAPSE_ON_RECLICK_KEY) !== 'false';
        } catch {
            return true;
        }
    });
    const [closeOtherMenus, setCloseOtherMenus] = useState(() => {
        try {
            return localStorage.getItem(CLOSE_OTHER_MENUS_KEY) !== 'false';
        } catch {
            return true;
        }
    });

    // Security inputs
    const [secCurrentPassword, setSecCurrentPassword] = useState('');
    const [secNewPassword, setSecNewPassword] = useState('');
    const [secConfirmPassword, setSecConfirmPassword] = useState('');
    const [secSaving, setSecSaving] = useState(false);
    const [secSuccess, setSecSuccess] = useState('');
    const [secError, setSecError] = useState('');

    // 2FA Setup
    const [secMfaSetup, setSecMfaSetup] = useState(null);
    const [secMfaQrDataUrl, setSecMfaQrDataUrl] = useState('');
    const [secMfaTotpCode, setSecMfaTotpCode] = useState('');
    const [secMfaSaving, setSecMfaSaving] = useState(false);
    const [secMfaError, setSecMfaError] = useState('');
    const [showSecMfaPanel, setShowSecMfaPanel] = useState(false);

    // Organization users & ownership
    const [organizationUsers, setOrganizationUsers] = useState([]);
    const [selectedNewOwner, setSelectedNewOwner] = useState('');
    const [ownershipSaving, setOwnershipSaving] = useState(false);
    const [ownershipSuccess, setOwnershipSuccess] = useState('');
    const [ownershipError, setOwnershipError] = useState('');
    const [organizationDeleteConfirmName, setOrganizationDeleteConfirmName] = useState('');


    const handleSaveProfile = async (e) => {
        e.preventDefault();
        if (!isPhoneNumberValid(profilePhone)) {
            setProfileError(PHONE_NUMBER_ERROR);
            setProfileSuccess('');
            return;
        }
        setProfileSaving(true);
        setProfileError('');
        setProfileSuccess('');
        try {
            const res = await apiFetch('/api/auth/me', {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    fullName: profileName,
                    title: profileTitle,
                    company: profileCompany,
                    phoneNumber: profilePhone,
                    themePreference: profileTheme
                })
            });
            if (res.ok) {
                setProfileSuccess('Profile saved successfully.');
                setProfileData(prev => ({
                    ...prev,
                    fullName: profileName,
                    title: profileTitle,
                    company: profileCompany,
                    phoneNumber: profilePhone,
                    themePreference: profileTheme
                }));
                localStorage.setItem('theme-preference', profileTheme);
            } else {
                const errs = await res.json();
                setProfileError(Array.isArray(errs) ? errs.map(x => x.description).join(' ') : 'Failed to update profile.');
            }
        } catch (err) {
            setProfileError('Connection error while saving profile.');
        } finally {
            setProfileSaving(false);
        }
    };

    const handleSavePassword = async (e) => {
        e.preventDefault();
        setSecSaving(true);
        setSecSuccess('');
        setSecError('');
        
        const checks = getPasswordValidity(secNewPassword);
        const isValid = Object.values(checks).every(Boolean);
        if (!isValid) {
            setSecError('New password does not meet complexity requirements.');
            setSecSaving(false);
            return;
        }

        if (secNewPassword !== secConfirmPassword) {
            setSecError('Passwords do not match.');
            setSecSaving(false);
            return;
        }

        try {
            const body = {
                newPassword: secNewPassword
            };
            if (profileData && profileData.hasPassword !== false) {
                body.currentPassword = secCurrentPassword;
            }
            const res = await apiFetch('/api/auth/security/change-password', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(body)
            });
            if (res.ok) {
                const data = await res.json();
                if (data.token) {
                    sessionStorage.setItem('token', data.token);
                }
                setSecSuccess(profileData?.hasPassword === false ? 'Password set successfully.' : 'Password updated successfully.');
                setSecCurrentPassword('');
                setSecNewPassword('');
                setSecConfirmPassword('');
                setProfileData(prev => ({ ...prev, hasPassword: true }));
            } else {
                const errs = await res.json();
                setSecError(Array.isArray(errs) ? errs.map(x => x.description).join(' ') : 'Failed to update password.');
            }
        } catch (err) {
            setSecError('Connection error while updating password.');
        } finally {
            setSecSaving(false);
        }
    };

    const handleInit2faSetup = async () => {
        setSecMfaError('');
        try {
            const res = await apiFetch('/api/auth/security/get-2fa-setup', { method: 'POST' });
            if (res.ok) {
                const data = await res.json();
                if (data.token) {
                    sessionStorage.setItem('token', data.token);
                }
                setSecMfaSetup(data);
                if (data.qrCodeUri) {
                    const qrUrl = await QRCode.toDataURL(data.qrCodeUri, { width: 180, margin: 1 });
                    setSecMfaQrDataUrl(qrUrl);
                }
                setShowSecMfaPanel(true);
            } else {
                setSecMfaError('Failed to fetch 2FA key details.');
            }
        } catch (err) {
            setSecMfaError('Failed to connect to the Identity service.');
        }
    };

    const handleConfirmEnable2fa = async (e) => {
        e.preventDefault();
        setSecMfaSaving(true);
        setSecMfaError('');
        try {
            const res = await apiFetch('/api/auth/security/enable-2fa', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ totpCode: secMfaTotpCode })
            });
            if (res.ok) {
                const data = await res.json();
                if (data.token) {
                    sessionStorage.setItem('token', data.token);
                }
                setProfileData(prev => ({ ...prev, twoFactorEnabled: true }));
                setShowSecMfaPanel(false);
                setSecMfaTotpCode('');
                setSecMfaSetup(null);
                setSecMfaQrDataUrl('');
                setSecSuccess('Two-factor authentication enabled successfully.');
            } else {
                const errs = await res.json();
                setSecMfaError(Array.isArray(errs) ? errs.map(x => x.description).join(' ') : 'Failed to verify 2FA code.');
            }
        } catch (err) {
            setSecMfaError('Connection error while enabling 2FA.');
        } finally {
            setSecMfaSaving(false);
        }
    };

    const handleDisable2fa = async () => {
        if (!await confirm('Are you sure you want to disable Two-Factor Authentication on your account? This reduces security.')) return;
        try {
            const res = await apiFetch('/api/auth/security/disable-2fa', { method: 'POST' });
            if (res.ok) {
                const data = await res.json();
                if (data.token) {
                    sessionStorage.setItem('token', data.token);
                }
                setProfileData(prev => ({ ...prev, twoFactorEnabled: false }));
                setSecSuccess('Two-factor authentication has been disabled.');
            } else {
                setSecError('Failed to disable 2FA.');
            }
        } catch (err) {
            setSecError('Connection error while disabling 2FA.');
        }
    };

    const handleTransferOwnership = async (e) => {
        e.preventDefault();
        if (!selectedNewOwner) return;
        const targetUser = organizationUsers.find(u => u.id === selectedNewOwner);
        if (!await confirm(`Are you sure you want to transfer Root Ownership of this organization to ${targetUser?.fullName || targetUser?.email}? You will no longer be the root administrator.`)) return;

        setOwnershipSaving(true);
        setOwnershipSuccess('');
        setOwnershipError('');
        try {
            const res = await apiFetch('/api/auth/organization/transfer-ownership', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ newRootAdminUserId: selectedNewOwner })
            });
            if (res.ok) {
                setOwnershipSuccess('Organization ownership successfully transferred.');
                const profileRes = await apiFetch('/api/auth/me');
                if (profileRes.ok) {
                    const data = await profileRes.json();
                    setProfileData(data);
                }
            } else {
                const errs = await res.json();
                setOwnershipError(Array.isArray(errs) ? errs.map(x => x.description).join(' ') : 'Failed to transfer ownership.');
            }
        } catch (err) {
            setOwnershipError('Connection error while transferring ownership.');
        } finally {
            setOwnershipSaving(false);
        }
    };

    const handleDeleteUserAccount = async () => {
        if (!await confirm('Are you sure you want to permanently delete your account? This action cannot be undone.')) return;
        try {
            const res = await apiFetch('/api/auth/me', { method: 'DELETE' });
            if (res.ok) {
                await alert('Your account has been deleted successfully.');
                handleLogout();
            } else {
                const errs = await res.json();
                alert(Array.isArray(errs) ? errs.map(x => x.description).join(' ') : 'Failed to delete account.');
            }
        } catch (err) {
            alert('Connection error while deleting account.');
        }
    };

    const handleDeleteOrganization = async (e) => {
        e.preventDefault();
        const activeOrganization = organizations.find(t => t.id === activeOrganizationId);
        const tName = activeOrganization?.name || activeOrganizationId;
        if (organizationDeleteConfirmName !== tName) {
            setOwnershipError(`Organization name mismatch. Please type exactly: "${tName}"`);
            return;
        }

        if (!await confirm(`CRITICAL WARNING: This will permanently delete the organization "${tName}" and all associated data, including all other member accounts. This action is absolute and cannot be undone. Do you proceed?`)) return;

        setOwnershipSaving(true);
        setOwnershipError('');
        try {
            const res = await apiFetch('/api/auth/organization', { method: 'DELETE' });
            if (res.ok) {
                await alert('Organization and all associated accounts have been deleted successfully.');
                handleLogout();
            } else {
                const errs = await res.json();
                setOwnershipError(Array.isArray(errs) ? errs.map(x => x.description).join(' ') : 'Failed to delete organization.');
            }
        } catch (err) {
            setOwnershipError('Connection error while deleting organization.');
        } finally {
            setOwnershipSaving(false);
        }
    };
    const [currentTool, setCurrentTool] = useState(() => {
        return localStorage.getItem('active-tool') || '';
    });
    const profileOverlayMouseDown = useRef(false);
    const adminOverlayMouseDown = useRef(false);

    // Active organization state and fetching
    const [organizations, setOrganizations] = useState([]);
    const [activeOrganizationId, setActiveOrganizationId] = useState('');
    const [activeOrganizationName, setActiveOrganizationName] = useState('');
    const [headerLogoUrl, setHeaderLogoUrl] = useState('');

    const handleOrganizationSwitch = async (targetOrganizationId) => {
        if (!targetOrganizationId) return;

        try {
            const res = await apiFetch('/api/auth/switch-organization', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ organizationId: targetOrganizationId })
            });
            if (res.ok) {
                const data = await res.json();
                sessionStorage.setItem('token', data.token);
                localStorage.removeItem('activeProjectId');
                localStorage.removeItem('activeProjectName');
                localStorage.removeItem('activeVersionId');
                localStorage.removeItem('activeVersionName');
                window.location.reload();
            } else {
                console.error('Failed to switch organization');
            }
        } catch (error) {
            console.error('Error switching organization', error);
        }
    };

    const handleOrganizationChange = async (e) => {
        await handleOrganizationSwitch(e.target.value);
    };

    // Active project state and fetching
    const [projects, setProjects] = useState([]);
    const [activeProjectId, setActiveProjectId] = useState(localStorage.getItem('activeProjectId') || '');

    const applyProjects = (data) => {
        const list = data || [];
        setProjects(list);
        const storedId = localStorage.getItem('activeProjectId') || '';
        if (!storedId) {
            setActiveProjectId('');
            return;
        }
        const match = list.find(p => String(p.id) === String(storedId));
        if (match) {
            setActiveProjectId(match.id);
            if (match.name) localStorage.setItem('activeProjectName', match.name);
            return;
        }
        const storedName = localStorage.getItem('activeProjectName') || '';
        const byName = storedName ? list.find(p => p.name === storedName) : null;
        if (byName) {
            localStorage.setItem('activeProjectId', byName.id);
            localStorage.setItem('activeProjectName', byName.name || '');
            setActiveProjectId(byName.id);
            window.dispatchEvent(new Event('activeProjectChanged'));
            return;
        }
        localStorage.removeItem('activeProjectId');
        localStorage.removeItem('activeProjectName');
        setActiveProjectId('');
        window.dispatchEvent(new Event('activeProjectChanged'));
    };

    const loadProjects = async () => {
        try {
            const res = await apiFetch('/api/projects');
            if (res.ok) {
                applyProjects(await res.json());
            } else {
                console.error('Failed to fetch projects in layout', res.status);
            }
        } catch (error) {
            console.error('Failed to fetch projects in layout', error);
        }
    };

    useEffect(() => {
        const handleGlobalProjectChange = async () => {
            setActiveProjectId(localStorage.getItem('activeProjectId') || '');
            await loadProjects();
        };
        window.addEventListener('activeProjectChanged', handleGlobalProjectChange);
        return () => window.removeEventListener('activeProjectChanged', handleGlobalProjectChange);
    }, []);

    const handleProjectSwitch = (id) => {
        const project = projects.find(p => String(p.id) === String(id));
        if (id) {
            localStorage.setItem('activeProjectId', id);
            localStorage.setItem('activeProjectName', project ? project.name : '');
            setActiveProjectId(id);
        } else {
            localStorage.removeItem('activeProjectId');
            localStorage.removeItem('activeProjectName');
            setActiveProjectId('');
        }
        window.dispatchEvent(new Event('activeProjectChanged'));
    };

    const toggleSidebar = () => {
        setIsCollapsed(prev => {
            const next = !prev;
            localStorage.setItem('sidebar-collapsed', String(next));
            return next;
        });
    };

    const sidebarCollapsedBeforeTableFocus = useRef(null);

    useEffect(() => {
        const handleTableFocusMode = (event) => {
            const focused = event.detail?.focused === true;
            if (focused) {
                setIsCollapsed(prev => {
                    if (sidebarCollapsedBeforeTableFocus.current === null) {
                        sidebarCollapsedBeforeTableFocus.current = prev;
                    }
                    if (prev) {
                        return prev;
                    }
                    localStorage.setItem('sidebar-collapsed', 'true');
                    return true;
                });
                return;
            }

            const previous = sidebarCollapsedBeforeTableFocus.current;
            sidebarCollapsedBeforeTableFocus.current = null;
            if (previous === false) {
                setIsCollapsed(false);
                localStorage.setItem('sidebar-collapsed', 'false');
            }
        };

        window.addEventListener('tableFocusModeChanged', handleTableFocusMode);
        return () => window.removeEventListener('tableFocusModeChanged', handleTableFocusMode);
    }, []);

    const [isAuthenticated, setIsAuthenticated] = useState(() => !!sessionStorage.getItem('token'));
    const [needsOnboarding, setNeedsOnboarding] = useState(() => (sessionStorage.getItem('token') ? null : false));
    const [customDialog, setCustomDialog] = useState({ isOpen: false, type: 'alert', message: '', resolve: null });

    useEffect(() => {
        if (!isAuthenticated) {
            setOrganizations([]);
            setActiveOrganizationId('');
            setActiveOrganizationName('');
            return;
        }

        if (needsOnboarding !== false) return;

        const token = sessionStorage.getItem('token');
        if (!token) return;

        const parsed = parseJwt(token);
        if (parsed && parsed.organization_id) {
            setActiveOrganizationId(parsed.organization_id);
        }

        const fetchOrganizations = async () => {
            try {
                const res = await apiFetch('/api/auth/organizations');
                if (res.ok) {
                    const data = await res.json();
                    setOrganizations(data);
                    const active = data.find(t => t.id === parsed?.organization_id);
                    if (active) {
                        setActiveOrganizationName(active.name);
                    }
                }
            } catch (error) {
                console.error('Failed to fetch organizations', error);
            }
        };
        fetchOrganizations();

        const refreshOrganizations = () => { fetchOrganizations(); };
        window.addEventListener('organization-logo-changed', refreshOrganizations);
        return () => window.removeEventListener('organization-logo-changed', refreshOrganizations);
    }, [isAuthenticated, needsOnboarding]);

    const activeLogoUpdatedAt = organizations.find(org => org.id === activeOrganizationId)?.logoUpdatedAt || '';

    useEffect(() => {
        if (!isAuthenticated || !activeOrganizationId || !activeLogoUpdatedAt) {
            setHeaderLogoUrl('');
            return undefined;
        }

        let cancelled = false;
        let objectUrl = '';
        const loadLogo = async () => {
            const res = await apiFetch('/api/auth/organization/logo');
            if (!res.ok || cancelled) return;
            const blob = await res.blob();
            objectUrl = URL.createObjectURL(blob);
            if (!cancelled) setHeaderLogoUrl(objectUrl);
        };
        loadLogo();
        return () => {
            cancelled = true;
            if (objectUrl) URL.revokeObjectURL(objectUrl);
        };
    }, [isAuthenticated, activeOrganizationId, activeLogoUpdatedAt]);

    useEffect(() => {
        if (!isAuthenticated || needsOnboarding !== false) return;
        if (!sessionStorage.getItem('token')) return;
        loadProjects();
    }, [isAuthenticated, needsOnboarding]);

    const handleCloseDialog = (result) => {
        if (customDialog.resolve) {
            customDialog.resolve(result);
        }
        setCustomDialog({ isOpen: false, type: 'alert', message: '', resolve: null });
    };

    // Member Invitation states (organization admin)
    const [invitations, setInvitations] = useState([]);
    const [inviteEmail, setInviteEmail] = useState('');
    const [inviteSaving, setInviteSaving] = useState(false);
    const [inviteError, setInviteError] = useState('');
    const [inviteSuccess, setInviteSuccess] = useState('');

    // Initial theme bootstrap from localStorage
    useEffect(() => {
        const savedTheme = localStorage.getItem('theme-preference') || 'system';
        document.documentElement.setAttribute('data-theme', savedTheme);
    }, []);

    const applyTheme = (theme) => {
        setProfileTheme(theme);
        localStorage.setItem('theme-preference', theme);
        document.documentElement.setAttribute('data-theme', theme);
    };

    const applyCollapseMenuOnReclick = (enabled) => {
        setCollapseMenuOnReclick(enabled);
        localStorage.setItem(COLLAPSE_ON_RECLICK_KEY, String(enabled));
    };

    const [isAdmin, setIsAdmin] = useState(null);
    const [isMember, setIsMember] = useState(true);
    const [accessRequest, setAccessRequest] = useState(null);
    const [pendingAccessCount, setPendingAccessCount] = useState(0);
    const [toolAccessList, setToolAccessList] = useState([]);
    const [primaryToolKey, setPrimaryToolKey] = useState('Logic');
    const [accessReady, setAccessReady] = useState(false);
    const accessMap = useMemo(() => toolAccessMap(toolAccessList), [toolAccessList]);

    const applyAccessFromProfile = useCallback((data) => {
        if (data?.token) {
            sessionStorage.setItem('token', data.token);
        }
        setIsAdmin(data?.isAdmin || false);
        setIsMember(data?.isMember !== false);
        setAccessRequest(data?.accessRequest || null);
        setToolAccessList(normalizeToolAccessList(data?.tools));
        setPrimaryToolKey(data?.primaryToolKey || 'Logic');
    }, []);

    const refreshAccess = useCallback(async () => {
        const res = await apiFetch('/api/auth/me');
        if (!res.ok) return;
        const data = await res.json();
        applyAccessFromProfile(data);
        return data;
    }, [applyAccessFromProfile]);

    useEffect(() => {
        if (!isAuthenticated) return;

        const refreshIfVisible = () => {
            if (document.visibilityState === 'visible') {
                refreshAccess();
            }
        };

        document.addEventListener('visibilitychange', refreshIfVisible);
        const intervalId = window.setInterval(refreshIfVisible, 30000);

        return () => {
            document.removeEventListener('visibilitychange', refreshIfVisible);
            window.clearInterval(intervalId);
        };
    }, [isAuthenticated, refreshAccess]);

    useEffect(() => {
        if (!isAuthenticated) return undefined;
        const canReview = !!isAdmin || accessMap.Organization?.access === 'Edit';
        if (!canReview) {
            setPendingAccessCount(0);
            return undefined;
        }

        let cancelled = false;
        const load = async () => {
            const res = await apiFetch('/api/auth/organization/access-requests');
            if (!res.ok) return;
            const rows = await res.json().catch(() => []);
            if (!cancelled) setPendingAccessCount(Array.isArray(rows) ? rows.length : 0);
        };
        load();
        const intervalId = window.setInterval(load, 30000);
        return () => {
            cancelled = true;
            window.clearInterval(intervalId);
        };
    }, [isAuthenticated, isAdmin, accessMap]);

    const pinTool = useCallback(async (toolKey) => {
        const res = await apiFetch(`/api/auth/me/tools/${toolKey}/enable`, { method: 'POST' });
        if (!res.ok) {
            const body = await res.json().catch(() => ({}));
            throw new Error(body.message || 'Unable to add tool.');
        }
        await refreshAccess();
    }, [refreshAccess]);

    const unpinTool = useCallback(async (toolKey) => {
        const res = await apiFetch(`/api/auth/me/tools/${toolKey}/disable`, { method: 'POST' });
        if (!res.ok) {
            const body = await res.json().catch(() => ({}));
            throw new Error(body.message || 'Unable to remove tool.');
        }
        await refreshAccess();
    }, [refreshAccess]);

    // Sync theme and admin status from database profile on startup
    useEffect(() => {
        if (!isAuthenticated) return;
        const syncProfile = async () => {
            try {
                const res = await apiFetch('/api/auth/me');
                if (res.ok) {
                    const data = await res.json();
                    applyAccessFromProfile(data);
                    setProfileData(data);
                    setProfileName(data.fullName || '');
                    setProfileTitle(data.title || '');
                    setProfileCompany(data.company || '');
                    setProfilePhone(data.phoneNumber || '');
                    setProfileTheme(data.themePreference || 'system');
                    if (data.themePreference) {
                        localStorage.setItem('theme-preference', data.themePreference);
                        document.documentElement.setAttribute('data-theme', data.themePreference);
                    }
                    setNeedsOnboarding(!!data.needsOnboarding);
                } else {
                    setIsAdmin(false);
                    setNeedsOnboarding(false);
                }
            } catch (err) {
                console.error('Failed to sync profile', err);
                setIsAdmin(false);
                setNeedsOnboarding(false);
            } finally {
                setAccessReady(true);
            }
        };
        syncProfile();
    }, [isAuthenticated, applyAccessFromProfile]);

    // Route protection and active-tool fail-safe
    useEffect(() => {
        if (location.pathname.startsWith('/admin') && isAuthenticated) {
            const checkAdmin = async () => {
                try {
                    const res = await apiFetch('/api/auth/me');
                    if (res.ok) {
                        const data = await res.json();
                        applyAccessFromProfile(data);
                        if (!data.isAdmin) {
                            navigate('/');
                        }
                    } else {
                        navigate('/');
                        setIsAdmin(false);
                    }
                } catch {
                    navigate('/');
                    setIsAdmin(false);
                }
            };
            checkAdmin();
        }
    }, [location.pathname, isAuthenticated, applyAccessFromProfile, navigate]);

    useEffect(() => {
        if (!accessReady || !isAuthenticated || isAdmin === null) return;

        const matchedToolKey = matchToolKey(location.pathname);
        if (matchedToolKey && !isToolEntitled(accessMap, matchedToolKey, isAdmin)) {
            const nextPath = firstAccessiblePath(accessMap, isAdmin);
            const nextTool = matchToolKey(nextPath);
            if (nextPath !== location.pathname && (!nextTool || isToolEntitled(accessMap, nextTool, isAdmin))) {
                navigate(nextPath);
            }
            return;
        }

        if (matchedToolKey && matchedToolKey !== currentTool) {
            setCurrentTool(matchedToolKey);
            localStorage.setItem('active-tool', matchedToolKey);
            return;
        }

        const storedTool = currentTool || localStorage.getItem('active-tool') || '';
        const storedIsValid = storedTool === 'Dashboard'
            || isToolEntitled(accessMap, storedTool, isAdmin);
        if (!matchedToolKey && !storedIsValid) {
            const fallback = isToolEntitled(accessMap, primaryToolKey, isAdmin) ? primaryToolKey : 'Dashboard';
            setCurrentTool(fallback);
            localStorage.setItem('active-tool', fallback);
        } else if (!currentTool && storedIsValid) {
            setCurrentTool(storedTool);
        }
    }, [accessReady, isAuthenticated, location.pathname, accessMap, isAdmin, currentTool, primaryToolKey, navigate]);

    const closeProfileModal = () => {
        setIsProfileModalOpen(false);
        const savedTheme = localStorage.getItem('theme-preference') || 'system';
        document.documentElement.setAttribute('data-theme', savedTheme);
    };

    const closeAdminModal = () => {
        setIsAdminModalOpen(false);
    };

    // Fetch profile details when Profile or Administration modal opens
    useEffect(() => {
        if (!isProfileModalOpen && !isAdminModalOpen) return;

        const loadProfileAndUsers = async () => {
            setProfileLoading(true);
            setProfileError('');
            setProfileSuccess('');
            setSecSuccess('');
            setSecError('');
            setOwnershipSuccess('');
            setOwnershipError('');
            setOrganizationDeleteConfirmName('');
            setSelectedNewOwner('');
            setInviteError('');
            setInviteSuccess('');
            
            try {
                const res = await apiFetch('/api/auth/me');
                if (res.ok) {
                    const data = await res.json();
                    applyAccessFromProfile(data);
                    setProfileData(data);
                    setProfileName(data.fullName || '');
                    setProfileTitle(data.title || '');
                    setProfileCompany(data.company || '');
                    setProfilePhone(data.phoneNumber || '');
                    setProfileTheme(data.themePreference || 'system');
                } else {
                    setProfileError('Failed to retrieve user profile data.');
                }
            } catch (err) {
                setProfileError('Failed to connect to the Identity service.');
            } finally {
                setProfileLoading(false);
            }
        };

        loadProfileAndUsers();
    }, [isProfileModalOpen, isAdminModalOpen]);

    // Close popovers when clicking outside
    useEffect(() => {
        const handleOutsideClick = (e) => {
            if (isPopoverOpen && !e.target.closest('#account-avatar-btn') && !e.target.closest('#account-popover')) {
                setIsPopoverOpen(false);
            }
            if (isNotificationsOpen && !e.target.closest('#notifications-btn') && !e.target.closest('#notifications-popover')) {
                setIsNotificationsOpen(false);
            }
            if (isMessagesOpen && !e.target.closest('#messages-btn') && !e.target.closest('#messages-popover')) {
                setIsMessagesOpen(false);
            }
        };
        document.addEventListener('mousedown', handleOutsideClick);
        return () => document.removeEventListener('mousedown', handleOutsideClick);
    }, [isPopoverOpen, isNotificationsOpen, isMessagesOpen]);

    // Listen to custom dialog events (alert and confirm)
    useEffect(() => {
        const handleShowAlert = (e) => {
            setCustomDialog({
                isOpen: true,
                type: 'alert',
                message: e.detail.message,
                resolve: e.detail.resolve
            });
        };
        const handleShowConfirm = (e) => {
            setCustomDialog({
                isOpen: true,
                type: 'confirm',
                message: e.detail.message,
                resolve: e.detail.resolve
            });
        };
        window.addEventListener('show-alert-modal', handleShowAlert);
        window.addEventListener('show-confirm-modal', handleShowConfirm);
        return () => {
            window.removeEventListener('show-alert-modal', handleShowAlert);
            window.removeEventListener('show-confirm-modal', handleShowConfirm);
        };
    }, []);

    const handleLogout = () => {
        sessionStorage.removeItem('token');
        localStorage.removeItem('activeProjectId');
        localStorage.removeItem('activeProjectName');
        localStorage.removeItem('activeVersionId');
        localStorage.removeItem('activeVersionName');
        window.location.href = '/';
    };

    const fetchInvitations = async () => {
        try {
            const res = await apiFetch('/api/auth/organization/invitations');
            if (res.ok) {
                const data = await res.json();
                setInvitations(data);
            }
        } catch (err) {
            console.error('Failed to fetch invitations', err);
        }
    };

    const handleInviteMember = async (e) => {
        e.preventDefault();
        setInviteSaving(true);
        setInviteError('');
        setInviteSuccess('');
        try {
            const res = await apiFetch('/api/auth/organization/invite', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email: inviteEmail })
            });
            if (res.ok) {
                const data = await res.json();
                setInviteSuccess(data.message || 'Invitation sent successfully.');
                if (data.invitationUrl) {
                    setInviteSuccess(
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem', textAlign: 'left' }}>
                            <p style={{ margin: 0 }}><i className="fa-solid fa-circle-check" style={{ color: 'var(--success)', marginRight: '0.25rem' }}></i> Invitation link created successfully!</p>
                            <div style={{ marginTop: '0.5rem', padding: '0.5rem', background: 'var(--bg-secondary)', borderRadius: 'var(--radius)', border: '1px solid var(--border)', fontSize: '0.8rem', wordBreak: 'break-all' }}>
                                <strong>Dev Invitation Link:</strong><br />
                                <a href={data.invitationUrl} style={{ color: 'var(--accent)', textDecoration: 'underline' }}>{data.invitationUrl}</a>
                            </div>
                        </div>
                    );
                }
                setInviteEmail('');
                await fetchInvitations();
            } else {
                const errData = await res.json();
                setInviteError(Array.isArray(errData) ? errData.map(e => e.description).join(' ') : 'Failed to send invitation.');
            }
        } catch (err) {
            setInviteError('Connection error while sending invitation.');
        } finally {
            setInviteSaving(false);
        }
    };

    const getPasswordValidity = (pwd) => {
        return {
            length: pwd.length >= 8,
            uppercase: /[A-Z]/.test(pwd),
            lowercase: /[a-z]/.test(pwd),
            number: /[0-9]/.test(pwd),
            special: /[^A-Za-z0-9]/.test(pwd)
        };
    };

    const productSections = useMemo(
        () => productNavSections(!!isAdmin).filter(section => isToolEntitled(accessMap, section.toolKey, isAdmin)),
        [accessMap, isAdmin]
    );
    const systemSections = useMemo(
        () => SYSTEM_NAV_SECTIONS.filter(section => isToolEntitled(accessMap, section.toolKey, isAdmin)),
        [accessMap, isAdmin]
    );
    const hasOrganizationAccess = isToolEntitled(accessMap, 'Organization', isAdmin);
    const canEditCurrent = canEditTool(accessMap, currentTool, isAdmin);
    const isViewOnly = accessReady
        && currentTool !== 'Dashboard'
        && currentTool !== 'Admin'
        && isToolEntitled(accessMap, currentTool, isAdmin)
        && !canEditCurrent;
    useEffect(() => {
        const match = [...productSections, ...systemSections].find(section => sectionMatchesPath(section, location.pathname));
        if (!match) return;
        setExpandedSectionIds(prev => {
            if (closeOtherMenus) {
                if (prev.size === 1 && prev.has(match.id)) return prev;
                const next = new Set([match.id]);
                writeExpandedSections(next);
                return next;
            }
            if (prev.has(match.id)) return prev;
            const next = new Set(prev);
            next.add(match.id);
            writeExpandedSections(next);
            return next;
        });
    }, [location.pathname, productSections, systemSections, closeOtherMenus]);

    const toggleSection = useCallback((id) => {
        setExpandedSectionIds(prev => {
            let next;
            if (closeOtherMenus) {
                next = prev.has(id) ? new Set() : new Set([id]);
            } else {
                next = new Set(prev);
                if (next.has(id)) next.delete(id);
                else next.add(id);
            }
            writeExpandedSections(next);
            return next;
        });
    }, [closeOtherMenus]);

    const expandSection = useCallback((id) => {
        setExpandedSectionIds(prev => {
            if (closeOtherMenus) {
                if (prev.size === 1 && prev.has(id)) return prev;
                const next = new Set([id]);
                writeExpandedSections(next);
                return next;
            }
            if (prev.has(id)) return prev;
            const next = new Set(prev);
            next.add(id);
            writeExpandedSections(next);
            return next;
        });
    }, [closeOtherMenus]);

    const applyCloseOtherMenus = (enabled) => {
        setCloseOtherMenus(enabled);
        localStorage.setItem(CLOSE_OTHER_MENUS_KEY, String(enabled));
        if (!enabled) return;
        setExpandedSectionIds(prev => {
            if (prev.size <= 1) return prev;
            const match = [...productSections, ...systemSections].find(section => sectionMatchesPath(section, location.pathname));
            const id = match ? match.id : [...prev][0];
            const next = new Set([id]);
            writeExpandedSections(next);
            return next;
        });
    };

    const accessContextValue = useMemo(() => ({
        tools: toolAccessList,
        accessMap,
        primaryToolKey,
        isSuperAdmin: !!isAdmin,
        isRootAdmin: !!profileData?.isRootAdmin,
        isMember,
        accessReady,
        canEditCurrent,
        currentToolKey: currentTool,
        pinTool,
        unpinTool,
        refresh: refreshAccess,
        refreshAccess
    }), [toolAccessList, accessMap, primaryToolKey, isAdmin, profileData?.isRootAdmin, isMember, accessReady, canEditCurrent, currentTool, pinTool, unpinTool, refreshAccess]);

    const renderCustomDialog = () => {
        if (!customDialog.isOpen) return null;

        const lowerMessage = customDialog.message.toLowerCase();
        let title = customDialog.type === 'confirm' ? 'Confirm Action' : 'System Message';
        let icon = customDialog.type === 'confirm' ? 'fa-solid fa-circle-question' : 'fa-solid fa-circle-info';
        let accentColor = 'var(--accent)';

        if (lowerMessage.includes('failed') || lowerMessage.includes('error') || lowerMessage.includes('cannot') || lowerMessage.includes('could not') || lowerMessage.includes('mismatch') || lowerMessage.includes('critical') || lowerMessage.includes('permanently') || lowerMessage.includes('delete')) {
            title = customDialog.type === 'confirm' ? 'Confirm Critical Action' : 'Action Failed';
            icon = 'fa-solid fa-triangle-exclamation';
            accentColor = 'var(--error)';
        } else if (lowerMessage.includes('success') || lowerMessage.includes('saved')) {
            title = 'Action Successful';
            icon = 'fa-solid fa-circle-check';
            accentColor = 'var(--success)';
        }

        return (
            <ModalPortal>
            <div 
                className="modal-overlay fade-in" 
                onClick={() => handleCloseDialog(false)}
                style={{ zIndex: 11000 }}
            >
                <div 
                    className="modal-content" 
                    onClick={e => e.stopPropagation()} 
                    style={{ 
                        minWidth: '380px', 
                        maxWidth: '480px',
                        display: 'flex', 
                        flexDirection: 'column', 
                        gap: '1.25rem',
                        padding: '1.25rem',
                        boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
                    }}
                >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', borderBottom: '1px solid var(--border)', paddingBottom: '0.75rem' }}>
                        <i className={icon} style={{ color: accentColor, fontSize: '1.25rem' }}></i>
                        <h3 style={{ margin: 0, fontSize: '1.1rem', fontWeight: 700, color: 'var(--text-primary)' }}>{title}</h3>
                    </div>
                    <div style={{ color: 'var(--text-primary)', lineHeight: '1.6', fontSize: '0.95rem', wordBreak: 'break-word', whiteSpace: 'pre-line' }}>
                        {customDialog.message}
                    </div>
                    <div className="modal-actions" style={{ marginTop: '0.5rem', display: 'flex', justifyContent: 'flex-end', gap: '0.75rem' }}>
                        {customDialog.type === 'confirm' && (
                            <button 
                                onClick={() => handleCloseDialog(false)} 
                                className="btn-secondary"
                                style={{ minWidth: '90px', padding: '0.5rem 1.25rem' }}
                            >
                                Cancel
                            </button>
                        )}
                        <button 
                            onClick={() => handleCloseDialog(true)} 
                            className="btn-primary"
                            style={{ 
                                minWidth: '90px', 
                                padding: '0.5rem 1.25rem',
                                background: accentColor === 'var(--error)' ? 'var(--error)' : 'var(--accent)'
                            }}
                            onMouseEnter={e => {
                                if (accentColor === 'var(--error)') {
                                    e.currentTarget.style.filter = 'brightness(0.9)';
                                }
                            }}
                            onMouseLeave={e => {
                                e.currentTarget.style.filter = 'none';
                            }}
                        >
                            {customDialog.type === 'confirm' ? 'Confirm' : 'OK'}
                        </button>
                    </div>
                </div>
            </div>
            </ModalPortal>
        );
    };

    if (location.pathname === '/changelog' && (!isAuthenticated || needsOnboarding !== false)) {
        return (
            <>
                <MaintenanceBanner />
                <ChangelogPage standalone />
            </>
        );
    }

    if (!isAuthenticated || needsOnboarding !== false) {
        return (
            <>
                <MaintenanceBanner />
                <AuthShell
                    forceOnboarding={needsOnboarding === true}
                    profileLoading={isAuthenticated && needsOnboarding === null}
                    onAuthenticated={({ needsOnboarding: next }) => {
                        setIsAuthenticated(true);
                        setNeedsOnboarding(!!next);
                    }}
                    onOnboardingComplete={() => setNeedsOnboarding(false)}
                />
                {renderCustomDialog()}
            </>
        );
    }

    if (!accessReady) {
        return (
            <>
                <MaintenanceBanner />
                <div style={{ minHeight: '100vh', background: 'var(--bg-primary)' }} />
            </>
        );
    }

    const waitingOnAccess = !isMember
        && !isAdmin
        && accessRequest
        && (accessRequest.status === 'Pending' || accessRequest.status === 'Rejected');

    if (waitingOnAccess) {
        return (
            <>
                <MaintenanceBanner />
                <AccessPending
                    status={accessRequest.status}
                    organizationName={accessRequest.organizationName}
                    onLogout={handleLogout}
                />
            </>
        );
    }

    return (
        <ToolAccessContext.Provider value={accessContextValue}>
        <div style={{ display: 'flex', flexDirection: 'column', height: '100vh', width: '100vw', background: 'var(--bg-primary)', color: 'var(--text-primary)' }}>
            <MaintenanceBanner />
            {/* Full-width Top Bar */}
            <header style={{
                height: '56px',
                width: '100%',
                borderBottom: '1px solid rgba(255, 255, 255, 0.12)',
                background: 'var(--bg-header)',
                color: 'var(--header-foreground)',
                padding: '0 1.5rem',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                flexShrink: 0,
                margin: 0
            }}>
                    {headerLogoUrl && (
                        <img
                            src={headerLogoUrl}
                            alt={activeOrganizationName || 'Organization logo'}
                            style={{ height: '32px', width: 'auto', maxWidth: '160px', objectFit: 'contain' }}
                        />
                    )}
                    {/* Context Selectors */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginLeft: 'auto' }}>
                        {/* Project Selector — silo context for product tools (not Admin / Organization) */}
                        {['Dashboard', 'Projects', 'Logic', 'Deployment', 'Variant', 'Pricing'].includes(currentTool) && (
                            <ProjectHeaderControl
                                projects={projects}
                                activeProjectId={activeProjectId}
                                onSwitch={handleProjectSwitch}
                            />
                        )}

                        {/* Organization Selector */}
                        {isAuthenticated && organizations.length > 0 && (
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                <span style={{ fontSize: '0.75rem', fontWeight: 700, color: 'var(--header-muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Organization</span>
                                <select
                                    value={activeOrganizationId}
                                    onChange={handleOrganizationChange}
                                    className="input-field"
                                    style={{
                                        padding: '0.3rem 2rem 0.3rem 0.6rem',
                                        fontSize: '0.85rem',
                                        width: 'auto',
                                        minWidth: '130px',
                                        background: '#FFFFFF',
                                        color: '#001f3d',
                                        borderRadius: '999px',
                                        border: 'none',
                                        cursor: 'pointer',
                                        margin: 0
                                    }}
                                >
                                    {organizations.map(t => (
                                        <option key={t.id} value={t.id}>{t.name}</option>
                                    ))}
                                </select>
                            </div>
                        )}

                        {/* Account Menu / Avatar */}
                        {isAuthenticated && (
                            <>
                                <div style={{
                                    width: '1px',
                                    height: '20px',
                                    background: 'rgba(255, 255, 255, 0.24)'
                                }} />
                                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                    
                                    {/* Notifications */}
                                    <div style={{ position: 'relative', display: 'inline-block' }}>
                                        <button
                                            id="notifications-btn"
                                            className="header-icon-btn"
                                            onClick={() => {
                                                setIsNotificationsOpen(!isNotificationsOpen);
                                                setIsPopoverOpen(false);
                                                setIsMessagesOpen(false);
                                            }}
                                            title="Notifications"
                                        >
                                            <i className="fa-regular fa-bell" style={{ fontSize: '1.2rem' }}></i>
                                            {notifications.some(n => n.unread) && (
                                                <span style={{
                                                    position: 'absolute',
                                                    top: '4px',
                                                    right: '4px',
                                                    width: '8px',
                                                    height: '8px',
                                                    background: 'var(--header-foreground)',
                                                    borderRadius: '50%',
                                                    border: '2px solid var(--bg-header)'
                                                }} />
                                            )}
                                        </button>
                                        
                                        {isNotificationsOpen && (
                                            <div
                                                id="notifications-popover"
                                                style={{
                                                    position: 'absolute',
                                                    top: '40px',
                                                    right: 0,
                                                    width: '300px',
                                                    background: 'var(--bg-card)',
                                                    border: '1px solid var(--border)',
                                                    borderRadius: 'var(--radius)',
                                                    boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                                                    zIndex: 1000,
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    padding: '0.25rem 0'
                                                }}
                                            >
                                                <div style={{ padding: '0.75rem 1rem', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                                    <span style={{ fontSize: '0.85rem', fontWeight: 600, color: 'var(--text-primary)' }}>Notifications</span>
                                                    {notifications.some(n => n.unread) && (
                                                        <button
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                setNotifications(notifications.map(n => ({ ...n, unread: false })));
                                                            }}
                                                            style={{
                                                                background: 'transparent',
                                                                border: 'none',
                                                                color: 'var(--accent)',
                                                                fontSize: '0.75rem',
                                                                cursor: 'pointer',
                                                                padding: 0
                                                            }}
                                                        >
                                                            Mark all as read
                                                        </button>
                                                    )}
                                                </div>
                                                <div style={{ maxHeight: '300px', overflowY: 'auto', padding: '0.25rem' }}>
                                                    {notifications.length === 0 ? (
                                                        <div style={{ padding: '2rem 1rem', textAlign: 'center', color: 'var(--text-secondary)', fontSize: '0.8rem' }}>
                                                            No notifications
                                                        </div>
                                                    ) : (
                                                        notifications.map(item => (
                                                            <div
                                                                key={item.id}
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    setNotifications(notifications.map(n => n.id === item.id ? { ...n, unread: false } : n));
                                                                }}
                                                                style={{
                                                                    padding: '0.6rem 0.8rem',
                                                                    display: 'flex',
                                                                    gap: '0.75rem',
                                                                    cursor: 'pointer',
                                                                    borderRadius: 'var(--radius)',
                                                                    background: item.unread ? 'var(--bg-secondary)' : 'transparent',
                                                                    transition: 'var(--transition)',
                                                                    alignItems: 'flex-start',
                                                                    marginBottom: '2px'
                                                                }}
                                                                onMouseEnter={e => {
                                                                    e.currentTarget.style.background = 'var(--bg-secondary)';
                                                                }}
                                                                onMouseLeave={e => {
                                                                    e.currentTarget.style.background = item.unread ? 'var(--bg-secondary)' : 'transparent';
                                                                }}
                                                            >
                                                                <div style={{
                                                                    width: '28px',
                                                                    height: '28px',
                                                                    borderRadius: '50%',
                                                                    background: item.unread ? 'var(--bg-primary)' : 'var(--bg-secondary)',
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    justifyContent: 'center',
                                                                    color: item.color,
                                                                    fontSize: '0.85rem',
                                                                    flexShrink: 0,
                                                                    marginTop: '2px',
                                                                    border: '1px solid var(--border)'
                                                                }}>
                                                                    <i className={item.icon}></i>
                                                                </div>
                                                                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.15rem', flex: 1 }}>
                                                                    <span style={{ fontSize: '0.8rem', fontWeight: item.unread ? 600 : 500, color: 'var(--text-primary)' }}>{item.title}</span>
                                                                    <span style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', lineHeight: '1.3' }}>{item.description}</span>
                                                                    <span style={{ fontSize: '0.7rem', color: 'var(--text-secondary)', marginTop: '0.2rem', opacity: 0.8 }}>{item.time}</span>
                                                                </div>
                                                            </div>
                                                        ))
                                                    )}
                                                </div>
                                            </div>
                                        )}
                                    </div>

                                    {/* Messages */}
                                    <div style={{ position: 'relative', display: 'inline-block' }}>
                                        <button
                                            id="messages-btn"
                                            className="header-icon-btn"
                                            onClick={() => {
                                                setIsMessagesOpen(!isMessagesOpen);
                                                setIsPopoverOpen(false);
                                                setIsNotificationsOpen(false);
                                            }}
                                            title="Messages"
                                        >
                                            <i className="fa-regular fa-envelope" style={{ fontSize: '1.2rem' }}></i>
                                            {messages.some(m => m.unread) && (
                                                <span style={{
                                                    position: 'absolute',
                                                    top: '4px',
                                                    right: '4px',
                                                    width: '8px',
                                                    height: '8px',
                                                    background: 'var(--header-foreground)',
                                                    borderRadius: '50%',
                                                    border: '2px solid var(--bg-header)'
                                                }} />
                                            )}
                                        </button>
                                        
                                        {isMessagesOpen && (
                                            <div
                                                id="messages-popover"
                                                style={{
                                                    position: 'absolute',
                                                    top: '40px',
                                                    right: 0,
                                                    width: '300px',
                                                    background: 'var(--bg-card)',
                                                    border: '1px solid var(--border)',
                                                    borderRadius: 'var(--radius)',
                                                    boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                                                    zIndex: 1000,
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    padding: '0.25rem 0'
                                                }}
                                            >
                                                <div style={{ padding: '0.75rem 1rem', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                                    <span style={{ fontSize: '0.85rem', fontWeight: 600, color: 'var(--text-primary)' }}>Messages</span>
                                                    {messages.some(m => m.unread) && (
                                                        <button
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                setMessages(messages.map(m => ({ ...m, unread: false })));
                                                            }}
                                                            style={{
                                                                background: 'transparent',
                                                                border: 'none',
                                                                color: 'var(--accent)',
                                                                fontSize: '0.75rem',
                                                                cursor: 'pointer',
                                                                padding: 0
                                                            }}
                                                        >
                                                            Mark all as read
                                                        </button>
                                                    )}
                                                </div>
                                                <div style={{ maxHeight: '300px', overflowY: 'auto', padding: '0.25rem' }}>
                                                    {messages.length === 0 ? (
                                                        <div style={{ padding: '2rem 1rem', textAlign: 'center', color: 'var(--text-secondary)', fontSize: '0.8rem' }}>
                                                            No messages
                                                        </div>
                                                    ) : (
                                                        messages.map(item => (
                                                            <div
                                                                key={item.id}
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    setMessages(messages.map(m => m.id === item.id ? { ...m, unread: false } : m));
                                                                }}
                                                                style={{
                                                                    padding: '0.6rem 0.8rem',
                                                                    display: 'flex',
                                                                    gap: '0.75rem',
                                                                    cursor: 'pointer',
                                                                    borderRadius: 'var(--radius)',
                                                                    background: item.unread ? 'var(--bg-secondary)' : 'transparent',
                                                                    transition: 'var(--transition)',
                                                                    alignItems: 'flex-start',
                                                                    marginBottom: '2px'
                                                                }}
                                                                onMouseEnter={e => {
                                                                    e.currentTarget.style.background = 'var(--bg-secondary)';
                                                                }}
                                                                onMouseLeave={e => {
                                                                    e.currentTarget.style.background = item.unread ? 'var(--bg-secondary)' : 'transparent';
                                                                }}
                                                            >
                                                                <div style={{
                                                                    width: '28px',
                                                                    height: '28px',
                                                                    borderRadius: '50%',
                                                                    background: 'var(--accent-glow)',
                                                                    color: 'var(--accent)',
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    justifyContent: 'center',
                                                                    fontSize: '0.75rem',
                                                                    fontWeight: 600,
                                                                    flexShrink: 0,
                                                                    marginTop: '2px',
                                                                    border: '1px solid var(--border)'
                                                                }}>
                                                                    {item.avatarInitials}
                                                                </div>
                                                                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.15rem', flex: 1 }}>
                                                                    <span style={{ fontSize: '0.8rem', fontWeight: item.unread ? 600 : 500, color: 'var(--text-primary)' }}>{item.sender}</span>
                                                                    <span style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', lineHeight: '1.3' }}>{item.text}</span>
                                                                    <span style={{ fontSize: '0.7rem', color: 'var(--text-secondary)', marginTop: '0.2rem', opacity: 0.8 }}>{item.time}</span>
                                                                </div>
                                                            </div>
                                                        ))
                                                    )}
                                                </div>
                                            </div>
                                        )}
                                    </div>

                                    {/* Help */}
                                    <button
                                        id="help-btn"
                                        className="header-icon-btn"
                                        onClick={() => alert("Help & Documentation is currently under development.")}
                                        title="Get Help"
                                    >
                                        <i className="fa-regular fa-circle-question" style={{ fontSize: '1.2rem' }}></i>
                                    </button>

                                    {/* Account settings */}
                                    <div style={{ position: 'relative', display: 'inline-block' }}>
                                        <button
                                            id="account-avatar-btn"
                                            className="header-icon-btn"
                                            onClick={() => {
                                                setIsPopoverOpen(!isPopoverOpen);
                                                setIsNotificationsOpen(false);
                                                setIsMessagesOpen(false);
                                            }}
                                            title="Account settings"
                                        >
                                            <i className="fa-regular fa-circle-user" style={{ fontSize: '1.2rem' }}></i>
                                        </button>

                                        {isPopoverOpen && (
                                            <div
                                                id="account-popover"
                                                style={{
                                                    position: 'absolute',
                                                    top: '40px',
                                                    right: 0,
                                                    width: '240px',
                                                    background: 'var(--bg-card)',
                                                    border: '1px solid var(--border)',
                                                    borderRadius: 'var(--radius)',
                                                    boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                                                    zIndex: 1000,
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    padding: '0.25rem 0'
                                                }}
                                            >
                                                <div style={{ padding: '0.75rem 1rem', borderBottom: '1px solid var(--border)', display: 'flex', flexDirection: 'column' }}>
                                                    <span style={{ fontSize: '0.85rem', fontWeight: 600, color: 'var(--text-primary)' }}>{profileData?.fullName || 'User Profile'}</span>
                                                    <span style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{profileData?.email || ''}</span>
                                                </div>
                                                <div style={{ padding: '0.25rem' }}>
                                                    <button
                                                        onClick={() => {
                                                            setIsPopoverOpen(false);
                                                            setIsProfileModalOpen(true);
                                                            setProfileActiveTab('general');
                                                        }}
                                                        className="popover-item"
                                                    >
                                                        <i className="fa-solid fa-circle-user" style={{ fontSize: '1rem', width: '1.25rem', textAlign: 'center' }}></i>
                                                        <span>My profile</span>
                                                    </button>
                                                    {hasOrganizationAccess && (
                                                        <button
                                                            onClick={() => {
                                                                setIsPopoverOpen(false);
                                                                navigate('/organization/members');
                                                            }}
                                                            className="popover-item"
                                                        >
                                                            <i className="fa-solid fa-building" style={{ fontSize: '1rem', width: '1.25rem', textAlign: 'center' }}></i>
                                                            <span>Organization</span>
                                                        </button>
                                                    )}
                                                    <button
                                                        onClick={() => {
                                                            setIsPopoverOpen(false);
                                                            setIsAdminModalOpen(true);
                                                            setAdminActiveTab('billing');
                                                        }}
                                                        className="popover-item"
                                                    >
                                                        <i className="fa-solid fa-credit-card" style={{ fontSize: '1rem', width: '1.25rem', textAlign: 'center' }}></i>
                                                        <span>Billing</span>
                                                    </button>
                                                    <div style={{ borderTop: '1px solid var(--border)', margin: '0.25rem 0' }} />
                                                    <button
                                                        onClick={() => {
                                                            setIsPopoverOpen(false);
                                                            handleLogout();
                                                        }}
                                                        className="popover-item popover-item-danger"
                                                    >
                                                        <i className="fa-solid fa-right-from-bracket" style={{ fontSize: '1rem', width: '1.25rem', textAlign: 'center' }}></i>
                                                        <span>Logout</span>
                                                    </button>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </>
                        )}
                    </div>
            </header>

            <div style={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
                {/* Minimalist Sidebar */}
                <nav style={{
                    width: isCollapsed ? '72px' : '268px',
                    borderRight: '1px solid var(--border)',
                    background: 'var(--bg-card)',
                    padding: isCollapsed ? '0.75rem 0.5rem' : '0.75rem',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '0.35rem',
                    transition: 'var(--transition)',
                    position: 'relative'
                }}>
                    <button
                        onClick={toggleSidebar}
                        style={{
                            position: 'absolute',
                            right: '-18px',
                            top: '1rem',
                            width: '40px',
                            height: '40px',
                            borderRadius: '999px',
                            border: '1px solid var(--border)',
                            background: 'var(--accent)',
                            color: 'white',
                            cursor: 'pointer',
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            boxShadow: '0 8px 20px var(--accent-glow)',
                            transition: 'var(--transition)',
                            zIndex: 30
                        }}
                        onMouseEnter={e => {
                            e.currentTarget.style.background = 'var(--accent-hover)';
                            e.currentTarget.style.transform = 'translateX(1px)';
                        }}
                        onMouseLeave={e => {
                            e.currentTarget.style.background = 'var(--accent)';
                            e.currentTarget.style.transform = 'none';
                        }}
                        title={isCollapsed ? "Expand Sidebar" : "Collapse Sidebar"}
                        aria-label={isCollapsed ? "Expand Sidebar" : "Collapse Sidebar"}
                    >
                        <i className={`fa-solid ${isCollapsed ? 'fa-chevron-right' : 'fa-chevron-left'}`} style={{ fontSize: '0.95rem' }}></i>
                    </button>

                    <div className="sidebar-nav-scroll">
                        <SidebarNav
                            sections={productSections}
                            pathname={location.pathname}
                            isCollapsed={isCollapsed}
                            expandedIds={expandedSectionIds}
                            onToggle={toggleSection}
                            onExpand={expandSection}
                            collapseOnReclick={collapseMenuOnReclick}
                        />
                        {systemSections.length > 0 && (
                            <>
                                <div className="sidebar-nav-divider" role="separator" />
                                <SidebarNav
                                    sections={systemSections}
                                    pathname={location.pathname}
                                    isCollapsed={isCollapsed}
                                    expandedIds={expandedSectionIds}
                                    onToggle={toggleSection}
                                    onExpand={expandSection}
                                    collapseOnReclick={collapseMenuOnReclick}
                                    badges={{ '/organization/members': pendingAccessCount }}
                                />
                            </>
                        )}
                        <div style={{ flex: 1 }} />
                    </div>

                    <div className="sidebar-nav-footer" style={{
                        borderTop: '1px solid var(--border)',
                        padding: isCollapsed ? '0.4rem 0' : '0.6rem 1rem 0.5rem',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: isCollapsed ? 'center' : 'flex-start',
                        gap: '0.35rem'
                    }}>
                        {!isCollapsed && (
                            <>
                                <img
                                    src={ruleStoreLogo}
                                    alt="RuleStore"
                                    style={{ height: '28px', width: 'auto', maxWidth: '100%' }}
                                />
                                <div style={{
                                    fontSize: '0.7rem',
                                    color: 'var(--text-secondary)',
                                    opacity: 0.7
                                }}>
                                    © RuleStore 2026
                                </div>
                            </>
                        )}
                        <div style={{
                            fontSize: '0.7rem',
                            color: 'var(--text-secondary)',
                            opacity: 0.5,
                            textAlign: isCollapsed ? 'center' : 'left',
                            fontFamily: 'monospace',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            width: '100%'
                        }}>
                            <VersionButton
                                collapsed={isCollapsed}
                                align={isCollapsed ? 'center' : 'left'}
                            />
                        </div>
                    </div>
                </nav>

                {/* Main Content Area */}
                <main style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', background: 'var(--bg-page)' }}>
                    <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', display: 'flex', flexDirection: 'column', position: 'relative' }}>
                        {isViewOnly && (
                            <div style={{
                                background: 'rgba(245, 158, 11, 0.1)',
                                borderBottom: '1px solid rgba(245, 158, 11, 0.3)',
                                color: 'var(--text-primary)',
                                padding: '0.55rem 1.5rem',
                                fontSize: '0.85rem',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.5rem',
                                flexShrink: 0
                            }}>
                                <i className="fa-solid fa-eye" style={{ color: '#F59E0B' }}></i>
                                <span><strong>View only</strong> — you can inspect this tool but cannot create, save, or delete.</span>
                            </div>
                        )}
                        <Outlet />
                    </div>
                </main>
            </div>

            {/* My Profile Modal */}
            {isProfileModalOpen && (
                <ModalPortal>
                <div 
                    className="modal-overlay fade-in" 
                    onMouseDown={(e) => {
                        if (e.target === e.currentTarget) profileOverlayMouseDown.current = true;
                    }}
                    onMouseUp={(e) => {
                        if (e.target === e.currentTarget && profileOverlayMouseDown.current) {
                            closeProfileModal();
                        }
                        profileOverlayMouseDown.current = false;
                    }}
                >
                    <div 
                        className="modal-content" 
                        style={{ 
                            maxWidth: '750px', 
                            width: '90%', 
                            height: '520px', 
                            display: 'flex', 
                            flexDirection: 'column', 
                            padding: 0,
                            overflow: 'hidden'
                        }}
                        onClick={e => e.stopPropagation()}
                    >
                        {/* Header */}
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.7rem 1rem', borderBottom: '1px solid var(--border)', background: 'var(--bg-secondary)' }}>
                            <h2 style={{ margin: 0, color: 'var(--text-primary)', fontSize: '1.25rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                <i className="fa-solid fa-circle-user" style={{ color: 'var(--accent)' }}></i>
                                My Profile
                            </h2>
                            <button 
                                className="btn-secondary" 
                                style={{ border: 'none', background: 'transparent', padding: '4px 8px', fontSize: '1.2rem', cursor: 'pointer', color: 'var(--text-secondary)' }} 
                                onClick={closeProfileModal}
                            >
                                <i className="fa-solid fa-xmark"></i>
                            </button>
                        </div>

                        {/* Split Body */}
                        <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
                            {/* Tabs sidebar */}
                            <div style={{ width: '220px', borderRight: '1px solid var(--border)', background: 'var(--bg-secondary)', padding: '1rem', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                                <button 
                                    type="button"
                                    onClick={() => setProfileActiveTab('general')}
                                    style={{
                                        background: profileActiveTab === 'general' ? 'var(--accent)' : 'transparent',
                                        color: profileActiveTab === 'general' ? 'white' : 'var(--text-primary)',
                                        border: 'none', padding: '0.75rem 1rem', borderRadius: 'var(--radius)', textAlign: 'left', cursor: 'pointer', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '0.5rem', transition: 'var(--transition)'
                                    }}
                                >
                                    <i className="fa-solid fa-address-card"></i> General Info
                                </button>

                                <button 
                                    type="button"
                                    onClick={() => setProfileActiveTab('preferences')}
                                    style={{
                                        background: profileActiveTab === 'preferences' ? 'var(--accent)' : 'transparent',
                                        color: profileActiveTab === 'preferences' ? 'white' : 'var(--text-primary)',
                                        border: 'none', padding: '0.75rem 1rem', borderRadius: 'var(--radius)', textAlign: 'left', cursor: 'pointer', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '0.5rem', transition: 'var(--transition)'
                                    }}
                                >
                                    <i className="fa-solid fa-sliders"></i> Preferences
                                </button>
                                
                                <button 
                                    type="button"
                                    onClick={() => setProfileActiveTab('security')}
                                    style={{
                                        background: profileActiveTab === 'security' ? 'var(--accent)' : 'transparent',
                                        color: profileActiveTab === 'security' ? 'white' : 'var(--text-primary)',
                                        border: 'none', padding: '0.75rem 1rem', borderRadius: 'var(--radius)', textAlign: 'left', cursor: 'pointer', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '0.5rem', transition: 'var(--transition)'
                                    }}
                                >
                                    <i className="fa-solid fa-shield-halved"></i> Security
                                </button>
                            </div>

                            {/* Content area */}
                            <div style={{ flex: 1, padding: '1.5rem', overflowY: 'auto', background: 'var(--bg-primary)' }}>
                                {profileLoading && (
                                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '2rem 0', gap: '1rem' }}>
                                        <div className="loader"></div>
                                        <p style={{ color: 'var(--text-secondary)', fontSize: '0.9rem' }}>Loading profile details...</p>
                                    </div>
                                )}

                                {!profileLoading && profileActiveTab === 'general' && profileData && (
                                    <form onSubmit={handleSaveProfile} style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
                                        <h3 style={{ margin: 0, color: 'var(--text-primary)', borderBottom: '1px solid var(--border)', paddingBottom: '0.5rem' }}>General Information</h3>
                                        
                                        {profileSuccess && (
                                            <div style={{ color: 'var(--success)', fontSize: '0.9rem', padding: '0.5rem 0.75rem', background: 'var(--bg-secondary)', borderRadius: 'var(--radius)', border: '1px solid var(--success)', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                                <i className="fa-solid fa-circle-check"></i>
                                                {profileSuccess}
                                            </div>
                                        )}
                                        {profileError && (
                                            <div style={{ color: 'var(--error)', fontSize: '0.9rem', padding: '0.5rem 0.75rem', background: 'var(--bg-secondary)', borderRadius: 'var(--radius)', border: '1px solid var(--error)', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                                <i className="fa-solid fa-triangle-exclamation"></i>
                                                {profileError}
                                            </div>
                                        )}

                                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                                            <div>
                                                <label style={{ display: 'block', fontWeight: 600, marginBottom: '0.25rem', fontSize: '0.85rem' }}>Full Name</label>
                                                <input type="text" className="input-field" value={profileName} onChange={e => setProfileName(e.target.value)} style={{ padding: '0.5rem 0.75rem', width: '100%', boxSizing: 'border-box' }} required />
                                            </div>
                                            <div>
                                                <label style={{ display: 'block', fontWeight: 600, marginBottom: '0.25rem', fontSize: '0.85rem' }} htmlFor="profile-phone">Mobile Number (MFA)</label>
                                                <PhoneNumberInput
                                                    id="profile-phone"
                                                    value={profilePhone}
                                                    onChange={setProfilePhone}
                                                    defaultCountry={profileData.countryCode}
                                                />
                                            </div>
                                        </div>

                                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                                            <div>
                                                <label style={{ display: 'block', fontWeight: 600, marginBottom: '0.25rem', fontSize: '0.85rem' }}>Job Title</label>
                                                <input type="text" className="input-field" value={profileTitle} onChange={e => setProfileTitle(e.target.value)} style={{ padding: '0.5rem 0.75rem', width: '100%', boxSizing: 'border-box' }} required />
                                            </div>
                                            <div>
                                                <label style={{ display: 'block', fontWeight: 600, marginBottom: '0.25rem', fontSize: '0.85rem' }}>Company / Organization</label>
                                                <input type="text" className="input-field" value={profileCompany} onChange={e => setProfileCompany(e.target.value)} style={{ padding: '0.5rem 0.75rem', width: '100%', boxSizing: 'border-box' }} required />
                                            </div>
                                        </div>

                                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                                            <div>
                                                <label style={{ display: 'block', fontWeight: 600, marginBottom: '0.25rem', fontSize: '0.85rem', color: 'var(--text-secondary)' }}>Email Address (read-only)</label>
                                                <input type="email" className="input-field" value={profileData.email} disabled style={{ padding: '0.5rem 0.75rem', opacity: 0.6, width: '100%', boxSizing: 'border-box' }} />
                                            </div>
                                            <div>
                                                <label style={{ display: 'block', fontWeight: 600, marginBottom: '0.25rem', fontSize: '0.85rem', color: 'var(--text-secondary)' }}>Active Organization (read-only)</label>
                                                <input type="text" className="input-field" value={profileData.organizationId} disabled style={{ padding: '0.5rem 0.75rem', opacity: 0.6, width: '100%', boxSizing: 'border-box' }} />
                                            </div>
                                        </div>

                                        <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.5rem' }}>
                                            <button type="submit" className="btn-primary" disabled={profileSaving} style={{ padding: '0.5rem 1.25rem' }}>
                                                {profileSaving ? 'Saving...' : 'Save General Info'}
                                            </button>
                                        </div>
                                    </form>
                                )}

                                {!profileLoading && profileActiveTab === 'preferences' && profileData && (
                                    <form onSubmit={handleSaveProfile} style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
                                        <h3 style={{ margin: 0, color: 'var(--text-primary)', borderBottom: '1px solid var(--border)', paddingBottom: '0.5rem' }}>User Preferences</h3>
                                        
                                        {profileSuccess && (
                                            <div style={{ color: 'var(--success)', fontSize: '0.9rem', padding: '0.5rem 0.75rem', background: 'var(--bg-secondary)', borderRadius: 'var(--radius)', border: '1px solid var(--success)', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                                <i className="fa-solid fa-circle-check"></i>
                                                {profileSuccess}
                                            </div>
                                        )}
                                        {profileError && (
                                            <div style={{ color: 'var(--error)', fontSize: '0.9rem', padding: '0.5rem 0.75rem', background: 'var(--bg-secondary)', borderRadius: 'var(--radius)', border: '1px solid var(--error)', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                                <i className="fa-solid fa-triangle-exclamation"></i>
                                                {profileError}
                                            </div>
                                        )}

                                        <label style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem', cursor: 'pointer' }}>
                                            <input
                                                type="checkbox"
                                                checked={closeOtherMenus}
                                                onChange={e => applyCloseOtherMenus(e.target.checked)}
                                                style={{ marginTop: '0.2rem' }}
                                            />
                                            <span>
                                                <span style={{ display: 'block', fontWeight: 600, fontSize: '0.85rem' }}>Close other menus</span>
                                                <span style={{ display: 'block', color: 'var(--text-secondary)', fontSize: '0.8rem', marginTop: '0.15rem' }}>
                                                    Opening a menu closes the other open menus. Turn this off to keep several menus open.
                                                </span>
                                            </span>
                                        </label>

                                        <label style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem', cursor: 'pointer' }}>
                                            <input
                                                type="checkbox"
                                                checked={collapseMenuOnReclick}
                                                onChange={e => applyCollapseMenuOnReclick(e.target.checked)}
                                                style={{ marginTop: '0.2rem' }}
                                            />
                                            <span>
                                                <span style={{ display: 'block', fontWeight: 600, fontSize: '0.85rem' }}>Collapse open menus</span>
                                                <span style={{ display: 'block', color: 'var(--text-secondary)', fontSize: '0.8rem', marginTop: '0.15rem' }}>
                                                    Clicking a menu item that is already open closes its submenu. Turn this off to keep submenus open.
                                                </span>
                                            </span>
                                        </label>

                                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                                            <div>
                                                <label style={{ display: 'block', fontWeight: 600, marginBottom: '0.25rem', fontSize: '0.85rem' }}>Theme Preference</label>
                                                <select 
                                                    className="input-field" 
                                                    value={profileTheme} 
                                                    onChange={e => applyTheme(e.target.value)}
                                                    style={{ padding: '0.5rem 0.75rem', width: '100%', boxSizing: 'border-box', background: 'var(--bg-secondary)', color: 'var(--text-primary)', border: '1px solid var(--border)', borderRadius: 'var(--radius)' }}
                                                >
                                                    <option value="system">Follow Desktop</option>
                                                    <option value="light">Light Mode</option>
                                                    <option value="dark">Dark Mode</option>
                                                </select>
                                            </div>
                                        </div>

                                        <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.5rem' }}>
                                            <button type="submit" className="btn-primary" disabled={profileSaving} style={{ padding: '0.5rem 1.25rem' }}>
                                                {profileSaving ? 'Saving...' : 'Save Preferences'}
                                            </button>
                                        </div>
                                    </form>
                                )}

                                {!profileLoading && profileActiveTab === 'security' && profileData && (
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '2rem' }}>
                                        {/* Change Password Card */}
                                        <div style={{ padding: '1.25rem', background: 'var(--bg-secondary)', borderRadius: 'var(--radius)', border: '1px solid var(--border)', display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                                            <h4 style={{ margin: 0, color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '1rem' }}>
                                                <i className="fa-solid fa-key" style={{ color: 'var(--accent)' }}></i> {profileData.hasPassword === false ? 'Set Password' : 'Change Password'}
                                            </h4>
                                            
                                            {profileData.hasPassword === false && (
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.5rem 0.75rem', background: 'rgba(59,130,246,0.1)', color: '#3B82F6', borderRadius: 'var(--radius)', border: '1px solid rgba(59,130,246,0.2)', fontSize: '0.85rem' }}>
                                                    <i className="fa-solid fa-circle-info"></i>
                                                    <span>You are authenticated via an external provider. A local password is not set for this account. Set a password below to enable password login.</span>
                                                </div>
                                            )}

                                            {secSuccess && (
                                                <div style={{ color: 'var(--success)', fontSize: '0.85rem', padding: '0.5rem 0.75rem', background: 'var(--bg-primary)', borderRadius: 'var(--radius)', border: '1px solid var(--success)', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                                    <i className="fa-solid fa-circle-check"></i>
                                                    {secSuccess}
                                                </div>
                                            )}
                                            {secError && (
                                                <div style={{ color: 'var(--error)', fontSize: '0.85rem', padding: '0.5rem 0.75rem', background: 'var(--bg-primary)', borderRadius: 'var(--radius)', border: '1px solid var(--error)', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                                    <i className="fa-solid fa-triangle-exclamation"></i>
                                                    {secError}
                                                </div>
                                            )}

                                            <form onSubmit={handleSavePassword} style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                                                {profileData.hasPassword !== false && (
                                                    <div>
                                                        <label style={{ display: 'block', fontWeight: 600, marginBottom: '0.25rem', fontSize: '0.8rem' }}>Current Password</label>
                                                        <input type="password" className="input-field" value={secCurrentPassword} onChange={e => setSecCurrentPassword(e.target.value)} required style={{ padding: '0.4rem 0.6rem', width: '100%', boxSizing: 'border-box' }} />
                                                    </div>
                                                )}

                                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                                                    <div>
                                                        <label style={{ display: 'block', fontWeight: 600, marginBottom: '0.25rem', fontSize: '0.8rem' }}>New Password</label>
                                                        <input type="password" className="input-field" value={secNewPassword} onChange={e => setSecNewPassword(e.target.value)} required style={{ padding: '0.4rem 0.6rem', width: '100%', boxSizing: 'border-box' }} />
                                                    </div>
                                                    <div>
                                                        <label style={{ display: 'block', fontWeight: 600, marginBottom: '0.25rem', fontSize: '0.8rem' }}>Confirm New Password</label>
                                                        <input type="password" className="input-field" value={secConfirmPassword} onChange={e => setSecConfirmPassword(e.target.value)} required style={{ padding: '0.4rem 0.6rem', width: '100%', boxSizing: 'border-box' }} />
                                                    </div>
                                                </div>

                                                {/* Password requirements checklist */}
                                                {secNewPassword && (
                                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '4px', paddingLeft: '4px' }}>
                                                        <div style={{ fontSize: '0.75rem', color: getPasswordValidity(secNewPassword).length ? 'var(--success)' : 'var(--text-secondary)', display: 'flex', alignItems: 'center', gap: '4px' }}>
                                                            <i className={`fa-solid ${getPasswordValidity(secNewPassword).length ? 'fa-circle-check' : 'fa-circle'}`}></i> Min 8 Chars
                                                        </div>
                                                        <div style={{ fontSize: '0.75rem', color: getPasswordValidity(secNewPassword).uppercase ? 'var(--success)' : 'var(--text-secondary)', display: 'flex', alignItems: 'center', gap: '4px' }}>
                                                            <i className={`fa-solid ${getPasswordValidity(secNewPassword).uppercase ? 'fa-circle-check' : 'fa-circle'}`}></i> Uppercase
                                                        </div>
                                                        <div style={{ fontSize: '0.75rem', color: getPasswordValidity(secNewPassword).lowercase ? 'var(--success)' : 'var(--text-secondary)', display: 'flex', alignItems: 'center', gap: '4px' }}>
                                                            <i className={`fa-solid ${getPasswordValidity(secNewPassword).lowercase ? 'fa-circle-check' : 'fa-circle'}`}></i> Lowercase
                                                        </div>
                                                        <div style={{ fontSize: '0.75rem', color: getPasswordValidity(secNewPassword).number ? 'var(--success)' : 'var(--text-secondary)', display: 'flex', alignItems: 'center', gap: '4px' }}>
                                                            <i className={`fa-solid ${getPasswordValidity(secNewPassword).number ? 'fa-circle-check' : 'fa-circle'}`}></i> Number (0-9)
                                                        </div>
                                                        <div style={{ fontSize: '0.75rem', color: getPasswordValidity(secNewPassword).special ? 'var(--success)' : 'var(--text-secondary)', display: 'flex', alignItems: 'center', gap: '4px' }}>
                                                            <i className={`fa-solid ${getPasswordValidity(secNewPassword).special ? 'fa-circle-check' : 'fa-circle'}`}></i> Special Char
                                                        </div>
                                                    </div>
                                                )}

                                                <button type="submit" className="btn-primary" disabled={secSaving || !Object.values(getPasswordValidity(secNewPassword)).every(Boolean)} style={{ padding: '0.4rem 1rem', alignSelf: 'flex-start' }}>
                                                    {secSaving ? 'Updating...' : (profileData.hasPassword === false ? 'Set Password' : 'Update Password')}
                                                </button>
                                            </form>
                                        </div>

                                        {/* Two-Factor Authentication Card */}
                                        <div style={{ padding: '1.25rem', background: 'var(--bg-secondary)', borderRadius: 'var(--radius)', border: '1px solid var(--border)', display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                                            <h4 style={{ margin: 0, color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '1rem' }}>
                                                <i className="fa-solid fa-shield-halved" style={{ color: 'var(--accent)' }}></i> Two-Factor Authentication (MFA)
                                            </h4>

                                            {profileData.twoFactorEnabled ? (
                                                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                                        <span style={{ background: 'rgba(16,185,129,0.1)', color: '#10B981', padding: '4px 8px', borderRadius: '4px', fontSize: '0.8rem', fontWeight: 'bold' }}>
                                                            <i className="fa-solid fa-lock" style={{ marginRight: '0.25rem' }}></i> Enabled (Secure)
                                                        </span>
                                                        <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>MFA is currently protecting your account login.</span>
                                                    </div>
                                                    <button onClick={handleDisable2fa} className="btn-secondary" style={{ alignSelf: 'flex-start', padding: '0.4rem 1rem', borderColor: 'var(--error)', color: 'var(--error)' }}
                                                        onMouseEnter={e => { e.currentTarget.style.background = 'rgba(239,68,68,0.05)'; }}
                                                        onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}>
                                                        Disable 2FA
                                                    </button>
                                                </div>
                                            ) : (
                                                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                                        <span style={{ background: 'rgba(245,158,11,0.1)', color: '#F59E0B', padding: '4px 8px', borderRadius: '4px', fontSize: '0.8rem', fontWeight: 'bold' }}>
                                                            <i className="fa-solid fa-triangle-exclamation" style={{ marginRight: '0.25rem' }}></i> Disabled (Vulnerable)
                                                        </span>
                                                        <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>Enhance your account security by generating a TOTP token setup.</span>
                                                    </div>
                                                    
                                                    {!showSecMfaPanel ? (
                                                        <button onClick={handleInit2faSetup} className="btn-primary" style={{ alignSelf: 'flex-start', padding: '0.4rem 1rem' }}>
                                                            Setup Two-Factor Auth
                                                        </button>
                                                    ) : (
                                                        <div style={{ marginTop: '0.5rem', padding: '1rem', background: 'var(--bg-primary)', borderRadius: 'var(--radius)', border: '1px solid var(--border)', display: 'flex', flexDirection: 'column', gap: '1rem', alignItems: 'center' }}>
                                                            <div style={{ fontSize: '0.85rem', fontWeight: 'bold', color: 'var(--text-primary)', textAlign: 'center' }}>
                                                                Scan QR Code with Authenticator App
                                                            </div>
                                                            {secMfaQrDataUrl ? (
                                                                <img src={secMfaQrDataUrl} alt="2FA QR Code" style={{ border: '4px solid white', borderRadius: '4px', width: '150px', height: '150px' }} />
                                                            ) : (
                                                                <div style={{ height: '150px', width: '150px', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'white', borderRadius: '4px' }}>
                                                                    <span style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>Generating QR...</span>
                                                                </div>
                                                            )}
                                                            {secMfaSetup && (
                                                                <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', textAlign: 'center', wordBreak: 'break-all' }}>
                                                                    Key: <code style={{ fontWeight: 'bold', background: 'var(--border)', padding: '2px 4px', borderRadius: '2px' }}>{secMfaSetup.sharedKey}</code>
                                                                </div>
                                                            )}
                                                            
                                                            {secMfaError && <div style={{ color: 'var(--error)', fontSize: '0.8rem' }}><i className="fa-solid fa-circle-exclamation" style={{ marginRight: '0.25rem' }}></i>{secMfaError}</div>}

                                                            <form onSubmit={handleConfirmEnable2fa} style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', width: '100%', maxWidth: '240px' }}>
                                                                <input type="text" className="input-field" placeholder="6-digit verification code" maxLength={6} required value={secMfaTotpCode} onChange={e => setSecMfaTotpCode(e.target.value)} style={{ textAlign: 'center', letterSpacing: '0.1em' }} />
                                                                <div style={{ display: 'flex', gap: '0.5rem' }}>
                                                                    <button type="submit" className="btn-primary" disabled={secMfaSaving} style={{ flex: 1, padding: '0.4rem' }}>
                                                                        {secMfaSaving ? 'Verifying...' : 'Verify & Enable'}
                                                                    </button>
                                                                    <button type="button" className="btn-secondary" onClick={() => setShowSecMfaPanel(false)} style={{ padding: '0.4rem' }}>
                                                                        Cancel
                                                                    </button>
                                                                </div>
                                                            </form>
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                        </div>

                                        {/* Danger Zone Card - Personal account deletion only */}
                                        <div style={{ padding: '1.25rem', background: 'rgba(239,68,68,0.02)', borderRadius: 'var(--radius)', border: '1px solid rgba(239,68,68,0.2)', display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
                                            <h4 style={{ margin: 0, color: 'var(--error)', display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '1rem' }}>
                                                <i className="fa-solid fa-triangle-exclamation"></i> Danger Zone
                                            </h4>

                                            {profileData.isRootAdmin ? (
                                                <div style={{ fontSize: '0.85rem', color: 'var(--text-primary)' }}>
                                                    <strong>Organization Owner Status</strong>: You are the root administrator of this organization. You cannot delete your account directly. Transfer ownership or delete the organization from Organization settings.
                                                </div>
                                            ) : (
                                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                                                        <span style={{ fontSize: '0.85rem', fontWeight: 'bold', color: 'var(--text-primary)' }}>Close Account</span>
                                                        <span style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>Permanently delete your profile and revoke access to this organization.</span>
                                                    </div>
                                                    <button onClick={handleDeleteUserAccount} className="btn-primary" style={{ background: 'var(--error)', padding: '0.4rem 1rem' }}>
                                                        Delete Account
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
                </ModalPortal>
            )}

            {/* Billing Modal */}
            {isAdminModalOpen && (
                <ModalPortal>
                <div 
                    className="modal-overlay fade-in" 
                    onMouseDown={(e) => {
                        if (e.target === e.currentTarget) adminOverlayMouseDown.current = true;
                    }}
                    onMouseUp={(e) => {
                        if (e.target === e.currentTarget && adminOverlayMouseDown.current) {
                            closeAdminModal();
                        }
                        adminOverlayMouseDown.current = false;
                    }}
                >
                    <div 
                        className="modal-content" 
                        style={{ 
                            maxWidth: '560px', 
                            width: '90%', 
                            display: 'flex', 
                            flexDirection: 'column', 
                            padding: 0,
                            overflow: 'hidden'
                        }}
                        onClick={e => e.stopPropagation()}
                    >
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.7rem 1rem', borderBottom: '1px solid var(--border)', background: 'var(--bg-secondary)' }}>
                            <h2 style={{ margin: 0, color: 'var(--text-primary)', fontSize: '1.25rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                <i className="fa-solid fa-credit-card" style={{ color: 'var(--accent)' }}></i>
                                Billing
                            </h2>
                            <button 
                                className="btn-secondary" 
                                style={{ border: 'none', background: 'transparent', padding: '4px 8px', fontSize: '1.2rem', cursor: 'pointer', color: 'var(--text-secondary)' }} 
                                onClick={closeAdminModal}
                            >
                                <i className="fa-solid fa-xmark"></i>
                            </button>
                        </div>

                        <div style={{ padding: '1.5rem', background: 'var(--bg-primary)', display: 'flex', flexDirection: 'column', gap: '1rem', opacity: 0.85 }}>
                            {hasOrganizationAccess && (
                                <p style={{ margin: 0, fontSize: '0.9rem', color: 'var(--text-secondary)' }}>
                                    Team invites, member access, and organization ownership live in the{' '}
                                    <button
                                        type="button"
                                        className="btn-secondary"
                                        style={{ padding: '0.15rem 0.5rem' }}
                                        onClick={() => {
                                            closeAdminModal();
                                            navigate('/organization/members');
                                        }}
                                    >
                                        Organization
                                    </button>
                                    {' '}tool.
                                </p>
                            )}
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid var(--border)', paddingBottom: '0.5rem' }}>
                                <h3 style={{ margin: 0 }}>Billing & Subscriptions</h3>
                                <span style={{ fontSize: '0.7rem', background: 'rgba(220,38,38,0.1)', color: 'var(--error)', padding: '2px 6px', borderRadius: '4px', fontWeight: 'bold' }}>Locked</span>
                            </div>
                            <p style={{ fontSize: '0.9rem', color: 'var(--text-secondary)', margin: 0 }}>Manage subscriptions, billing cycles, view invoice receipts, and update corporate credit card/bank account setups.</p>
                            <div style={{ padding: '1rem', background: 'var(--bg-secondary)', borderRadius: 'var(--radius)', border: '1px dashed var(--border)' }}>
                                <div style={{ fontWeight: 'bold', fontSize: '0.95rem' }}>Free Tier Developer Sandbox</div>
                                <div style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', marginTop: '0.25rem' }}>Allows unlimited variables and up to 3 active families.</div>
                            </div>
                            <button className="btn-primary" disabled style={{ cursor: 'not-allowed', alignSelf: 'flex-start', padding: '0.5rem 1rem' }}>Upgrade to Enterprise Plan</button>
                        </div>
                    </div>
                </div>
                </ModalPortal>
            )}
            {renderCustomDialog()}
        </div>
        </ToolAccessContext.Provider>
    );
}
